/**
 * Plot thread × chapter intensity grid for planning.
 * Source of truth: `book.metadata.plotGrid`.
 */

import type { Book } from "./models";

export type PlotThread = {
  id: string;
  name: string;
  color?: string;
  notes?: string;
};

export type PlotCell = {
  threadId: string;
  chapterId: string;
  note: string;
  /** 0 = empty, 1–3 = rising presence. */
  intensity: 0 | 1 | 2 | 3;
};

export type PlotGridState = {
  threads: PlotThread[];
  cells: PlotCell[];
};

export type PlotHeatmapCell = PlotCell & {
  chapterTitle: string;
  threadName: string;
};

const THREAD_COLORS = ["#3b82f6", "#a855f7", "#ef4444", "#22c55e", "#f59e0b", "#06b6d4", "#ec4899"];

export function generatePlotThreadId(): string {
  return `plot-thread-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function clampIntensity(value: unknown): 0 | 1 | 2 | 3 {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  if (n >= 3) return 3;
  return Math.round(n) as 0 | 1 | 2 | 3;
}

function normalizeThread(raw: unknown, index: number): PlotThread | null {
  if (!raw || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  const id = typeof record.id === "string" && record.id.trim() ? record.id.trim() : `plot-thread-${index + 1}`;
  const name = typeof record.name === "string" && record.name.trim() ? record.name.trim() : `Thread ${index + 1}`;
  const thread: PlotThread = { id, name };
  if (typeof record.color === "string" && record.color.trim()) thread.color = record.color.trim();
  if (typeof record.notes === "string") thread.notes = record.notes;
  return thread;
}

function normalizeCell(raw: unknown): PlotCell | null {
  if (!raw || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  const threadId = typeof record.threadId === "string" ? record.threadId.trim() : "";
  const chapterId = typeof record.chapterId === "string" ? record.chapterId.trim() : "";
  if (!threadId || !chapterId) return null;
  return {
    threadId,
    chapterId,
    note: typeof record.note === "string" ? record.note : "",
    intensity: clampIntensity(record.intensity),
  };
}

export function normalizePlotGrid(value: unknown): PlotGridState {
  if (!value || typeof value !== "object") {
    return { threads: [], cells: [] };
  }
  const record = value as Record<string, unknown>;
  const threads = Array.isArray(record.threads)
    ? record.threads.map(normalizeThread).filter((t): t is PlotThread => Boolean(t))
    : [];
  const cells = Array.isArray(record.cells)
    ? record.cells.map(normalizeCell).filter((c): c is PlotCell => Boolean(c))
    : [];
  return { threads, cells };
}

export function getPlotGrid(book: Pick<Book, "metadata">): PlotGridState {
  return normalizePlotGrid(book?.metadata?.plotGrid);
}

/** Ensure `book.metadata.plotGrid` exists and is normalized. Mutates book.metadata. */
export function ensurePlotGridState(book: Book): PlotGridState {
  const grid = getPlotGrid(book);
  book.metadata = {
    ...book.metadata,
    plotGrid: grid,
  };
  return grid;
}

function withPlotGrid(book: Book, grid: PlotGridState): Book {
  return {
    ...book,
    metadata: {
      ...book.metadata,
      plotGrid: {
        threads: grid.threads,
        cells: grid.cells,
      },
    },
  };
}

/** Upsert a plot thread by id (or append when missing). */
export function setPlotThread(book: Book, thread: PlotThread): Book {
  const grid = ensurePlotGridState(book);
  const index = grid.threads.findIndex((item) => item.id === thread.id);
  const threads =
    index >= 0
      ? grid.threads.map((item, i) => (i === index ? { ...item, ...thread, id: thread.id } : item))
      : [...grid.threads, thread];
  return withPlotGrid(book, { threads, cells: grid.cells });
}

/** Upsert a cell keyed by threadId + chapterId. */
export function setPlotCell(book: Book, cell: PlotCell): Book {
  const grid = ensurePlotGridState(book);
  const nextCell: PlotCell = {
    threadId: cell.threadId,
    chapterId: cell.chapterId,
    note: cell.note ?? "",
    intensity: clampIntensity(cell.intensity),
  };
  const index = grid.cells.findIndex(
    (item) => item.threadId === nextCell.threadId && item.chapterId === nextCell.chapterId
  );
  const cells =
    index >= 0
      ? grid.cells.map((item, i) => (i === index ? nextCell : item))
      : [...grid.cells, nextCell];
  return withPlotGrid(book, { threads: grid.threads, cells });
}

/**
 * Seed a heatmap from chapter tags + summaries when the grid is empty.
 * Tags become threads; chapters that mention a tag (or share a tag) get intensity 1–2.
 */
export function buildPlotHeatmap(book: Book): PlotHeatmapCell[] {
  const existing = getPlotGrid(book);
  if (existing.threads.length > 0 || existing.cells.length > 0) {
    const chapterById = new Map(
      (Array.isArray(book.chapters) ? book.chapters : []).map((ch) => [ch.id, ch])
    );
    const threadById = new Map(existing.threads.map((t) => [t.id, t]));
    return existing.cells.map((cell) => ({
      ...cell,
      chapterTitle: chapterById.get(cell.chapterId)?.title ?? cell.chapterId,
      threadName: threadById.get(cell.threadId)?.name ?? cell.threadId,
    }));
  }

  const chapters = Array.isArray(book.chapters) ? book.chapters : [];
  const tagCounts = new Map<string, number>();
  for (const chapter of chapters) {
    const tags = Array.isArray(chapter?.metadata?.tags) ? chapter.metadata.tags : [];
    for (const tag of tags) {
      const key = String(tag || "").trim();
      if (!key) continue;
      tagCounts.set(key, (tagCounts.get(key) ?? 0) + 1);
    }
  }

  const sortedTags = [...tagCounts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, 8)
    .map(([name]) => name);

  if (sortedTags.length === 0) {
    // Fall back to a single "Main plot" thread seeded from summaries.
    const thread: PlotThread = {
      id: generatePlotThreadId(),
      name: "Main plot",
      color: THREAD_COLORS[0],
    };
    const cells: PlotCell[] = chapters
      .filter((ch) => String(ch?.tabData?.summary || "").trim().length > 0)
      .map((ch) => {
        const summary = String(ch.tabData.summary || "");
        const intensity = (summary.length > 280 ? 2 : 1) as 1 | 2;
        return {
          threadId: thread.id,
          chapterId: ch.id,
          note: summary.slice(0, 160),
          intensity,
        };
      });
    return cells.map((cell) => ({
      ...cell,
      chapterTitle: chapters.find((c) => c.id === cell.chapterId)?.title ?? cell.chapterId,
      threadName: thread.name,
    }));
  }

  const threads: PlotThread[] = sortedTags.map((name, index) => ({
    id: generatePlotThreadId(),
    name,
    color: THREAD_COLORS[index % THREAD_COLORS.length],
  }));
  const threadByTag = new Map(sortedTags.map((tag, i) => [tag.toLowerCase(), threads[i]!]));

  const cells: PlotCell[] = [];
  for (const chapter of chapters) {
    const tags = Array.isArray(chapter?.metadata?.tags) ? chapter.metadata.tags : [];
    const summary = String(chapter?.tabData?.summary || "").toLowerCase();
    for (const tag of tags) {
      const key = String(tag || "").trim().toLowerCase();
      const thread = threadByTag.get(key);
      if (!thread) continue;
      const mentionedInSummary = summary.includes(key);
      cells.push({
        threadId: thread.id,
        chapterId: chapter.id,
        note: mentionedInSummary ? String(chapter.tabData?.summary || "").slice(0, 160) : "",
        intensity: mentionedInSummary ? 2 : 1,
      });
    }
  }

  return cells.map((cell) => ({
    ...cell,
    chapterTitle: chapters.find((c) => c.id === cell.chapterId)?.title ?? cell.chapterId,
    threadName: threads.find((t) => t.id === cell.threadId)?.name ?? cell.threadId,
  }));
}

/** Apply heatmap seed into book.metadata.plotGrid when empty. */
export function seedPlotGridFromChapters(book: Book): Book {
  const existing = getPlotGrid(book);
  if (existing.threads.length > 0 || existing.cells.length > 0) {
    return withPlotGrid(book, existing);
  }

  const chapters = Array.isArray(book.chapters) ? book.chapters : [];
  const tagCounts = new Map<string, number>();
  for (const chapter of chapters) {
    const tags = Array.isArray(chapter?.metadata?.tags) ? chapter.metadata.tags : [];
    for (const tag of tags) {
      const key = String(tag || "").trim();
      if (!key) continue;
      tagCounts.set(key, (tagCounts.get(key) ?? 0) + 1);
    }
  }

  const sortedTags = [...tagCounts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, 8)
    .map(([name]) => name);

  let threads: PlotThread[];
  let cells: PlotCell[];

  if (sortedTags.length === 0) {
    const thread: PlotThread = {
      id: generatePlotThreadId(),
      name: "Main plot",
      color: THREAD_COLORS[0],
    };
    threads = [thread];
    cells = chapters
      .filter((ch) => String(ch?.tabData?.summary || "").trim().length > 0)
      .map((ch) => {
        const summary = String(ch.tabData.summary || "");
        return {
          threadId: thread.id,
          chapterId: ch.id,
          note: summary.slice(0, 160),
          intensity: (summary.length > 280 ? 2 : 1) as 1 | 2,
        };
      });
  } else {
    threads = sortedTags.map((name, index) => ({
      id: generatePlotThreadId(),
      name,
      color: THREAD_COLORS[index % THREAD_COLORS.length],
    }));
    const threadByTag = new Map(sortedTags.map((tag, i) => [tag.toLowerCase(), threads[i]!]));
    cells = [];
    for (const chapter of chapters) {
      const tags = Array.isArray(chapter?.metadata?.tags) ? chapter.metadata.tags : [];
      const summary = String(chapter?.tabData?.summary || "").toLowerCase();
      for (const tag of tags) {
        const key = String(tag || "").trim().toLowerCase();
        const thread = threadByTag.get(key);
        if (!thread) continue;
        const mentionedInSummary = summary.includes(key);
        cells.push({
          threadId: thread.id,
          chapterId: chapter.id,
          note: mentionedInSummary ? String(chapter.tabData?.summary || "").slice(0, 160) : "",
          intensity: mentionedInSummary ? 2 : 1,
        });
      }
    }
  }

  return withPlotGrid(book, { threads, cells });
}
